import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Heart, MapPin } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';

export default function ProductCard({ product }) {
  const { user } = useAuth();
  const price = product.discount_price || product.price;
  const hasDiscount = product.discount_price && product.discount_price < product.price;
  const [inWishlist, setInWishlist] = useState(product.in_wishlist || false);
  
  const FALLBACK = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Crect width='400' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='48' fill='%23d1d5db'%3E🔧%3C/text%3E%3C/svg%3E";
  const image = product.images?.[0]?.url || FALLBACK;

  const toggleWishlist = async (e) => {
    e.preventDefault();
    if (!user) return;
    try {
      if (inWishlist) {
        await api.delete(`/wishlist/${product.id}`);
        setInWishlist(false);
      } else {
        await api.post('/wishlist', { product_id: product.id });
        setInWishlist(true);
      }
    } catch {}
  };

  return (
    <Link 
      to={`/products/${product.id}`}
      className="bg-white rounded-xl overflow-hidden shadow-sm card-hover block"
    >
      {/* Image */}
      <div className="relative aspect-square bg-gray-100">
        <img 
          src={image} 
          alt={product.title}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.onerror=null; e.target.src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400' viewBox='0 0 400 400'%3E%3Crect width='400' height='400' fill='%23f3f4f6'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='48' fill='%23d1d5db'%3E🔧%3C/text%3E%3C/svg%3E"; }}
        />
        
        {hasDiscount && (
          <span className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
            -{Math.round((1 - product.discount_price / product.price) * 100)}%
          </span>
        )}
        
        <button 
          onClick={toggleWishlist}
          className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-sm hover:bg-white transition"
        >
          <Heart className={`w-4 h-4 transition-colors ${inWishlist ? 'fill-primary text-primary' : ''}`} />
        </button>
        
        {product.seller?.is_verified && (
          <span className="absolute bottom-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
            ✓ Verificado
          </span>
        )}
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-sm line-clamp-2 mb-2 h-10">
          {product.title}
        </h3>
        
        {/* Price */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-lg font-bold text-primary">
            ₡{price.toLocaleString('es-CR')}
          </span>
          {product.negotiable && (
            <span className="text-xs text-gray-500">Negociable</span>
          )}
        </div>
        
        {/* Condition badge */}
        <span className={`inline-block text-xs px-2 py-1 rounded-full mb-2 ${
          product.condition === 'NEW' 
            ? 'bg-green-100 text-green-700' 
            : 'bg-orange-100 text-orange-700'
        }`}>
          {product.condition === 'NEW' ? 'Nuevo' : 'Usado'}
        </span>
        
        {/* Location */}
        <div className="flex items-center gap-1 text-xs text-gray-500">
          <MapPin className="w-3 h-3" />
          <span>{product.province?.replace('_', ' ')}</span>
        </div>
        
        {/* Rating */}
        {product.avg_rating > 0 && (
          <div className="flex items-center gap-1 mt-2">
            <div className="flex">
              {[1, 2, 3, 4, 5].map(i => (
                <Star 
                  key={i} 
                  className={`w-3 h-3 ${i <= Math.round(product.avg_rating) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} 
                />
              ))}
            </div>
            <span className="text-xs text-gray-500">({product.review_count})</span>
          </div>
        )}
        
        {/* Seller */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t">
          <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center text-xs font-bold">
            {product.seller?.name?.charAt(0) || '?'}
          </div>
          <span className="text-xs text-gray-600">{product.seller?.name}</span>
        </div>
      </div>
    </Link>
  );
}